import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { ChevronRight, ChevronLeft, Search, type LucideIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export interface TransferItem {
  id: number
  label: string
  subtitle?: string
}

interface TransferListProps {
  title: string
  icon: LucideIcon
  description: string
  availableItems: TransferItem[]
  assignedItems: TransferItem[]
  onTransfer: (itemIds: number[], toAssigned: boolean) => void
  disabled?: boolean
}

export function TransferList({
  title,
  icon: Icon,
  description,
  availableItems,
  assignedItems,
  onTransfer,
  disabled = false,
}: TransferListProps) {
  const [selectedAvailable, setSelectedAvailable] = useState<number[]>([])
  const [selectedAssigned, setSelectedAssigned] = useState<number[]>([])
  const [search, setSearch] = useState('')

  const handleSelectAvailable = (itemId: number) => {
    setSelectedAvailable((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    )
  }

  const handleSelectAssigned = (itemId: number) => {
    setSelectedAssigned((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    )
  }

  const handleMoveToAssigned = () => {
    if (selectedAvailable.length > 0) {
      onTransfer(selectedAvailable, true)
      setSelectedAvailable([])
    }
  }

  const handleMoveToAvailable = () => {
    if (selectedAssigned.length > 0) {
      onTransfer(selectedAssigned, false)
      setSelectedAssigned([])
    }
  }

  const renderItem = (
    item: TransferItem,
    isSelected: boolean,
    onSelect: (id: number) => void
  ) => (
    <div
      key={item.id}
      onClick={() => !disabled && onSelect(item.id)}
      className={cn(
        'flex items-center rounded-md p-2 cursor-pointer transition-colors',
        disabled && 'cursor-not-allowed opacity-50',
        isSelected
          ? 'bg-teal-500/10 border border-teal-500'
          : 'bg-background hover:bg-accent border border-transparent'
      )}
    >
      <span className="flex-1 text-sm">{item.label}</span>
      {item.subtitle && (
        <span className="text-xs text-muted-foreground">{item.subtitle}</span>
      )}
    </div>
  )

  const searchLower = search.toLowerCase()
  const filteredAvailable = search
    ? availableItems.filter((item) => item.label.toLowerCase().includes(searchLower))
    : availableItems
  const filteredAssigned = search
    ? assignedItems.filter((item) => item.label.toLowerCase().includes(searchLower))
    : assignedItems

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-medium">{title}</h4>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder={`Buscar ${title.toLowerCase()}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
          className="pl-9 h-8 text-sm"
        />
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-start">
        {/* Left Column: Available Items */}
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground">Disponibles</Label>
          <div className="max-h-60 space-y-2 overflow-y-auto rounded-md border p-3 bg-muted/30">
            {filteredAvailable.length > 0 ? (
              filteredAvailable.map((item) =>
                renderItem(item, selectedAvailable.includes(item.id), handleSelectAvailable)
              )
            ) : (
              <p className="text-xs text-center text-muted-foreground py-4">
                No hay {title.toLowerCase()} disponibles
              </p>
            )}
          </div>
        </div>

        {/* Middle Column: Transfer Buttons */}
        <div className="flex flex-col gap-2 justify-center pt-6">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleMoveToAssigned}
            disabled={disabled || selectedAvailable.length === 0}
            className="h-8 w-8"
            title="Transferir a asignados"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleMoveToAvailable}
            disabled={disabled || selectedAssigned.length === 0}
            className="h-8 w-8"
            title="Transferir a disponibles"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>

        {/* Right Column: Assigned Items */}
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground">Asignados</Label>
          <div className="max-h-60 space-y-2 overflow-y-auto rounded-md border p-3 bg-teal-500/5">
            {filteredAssigned.length > 0 ? (
              filteredAssigned.map((item) =>
                renderItem(item, selectedAssigned.includes(item.id), handleSelectAssigned)
              )
            ) : (
              <p className="text-xs text-center text-muted-foreground py-4">
                Sin {title.toLowerCase()} asignados
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
